// Sales dashboard → proofs with the client a day or more (Stephanie
// 2026-10-14), each with a one-click reminder to the people it went to.
// The rep's own projects. Hidden when there's nothing waiting.
import { useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, ApiError } from "@/lib/api";
import { timeAgo } from "@/lib/utils";

interface WaitingProof {
  proofId: string;
  version: number;
  item: string;
  clientName: string;
  projectId: string;
  projectName: string;
  sentAt: string | null;
  viewedAt: string | null;
  lastReminderAt: string | null;
}

const btn =
  "inline-flex h-9 items-center rounded-md border border-rule bg-white px-3 text-sm font-medium text-ink hover:bg-canvas disabled:opacity-50";

export function ProofsWaiting() {
  const queryClient = useQueryClient();
  const q = useQuery({
    queryKey: ["sales", "proofs-waiting"],
    queryFn: () => api<{ proofs: WaitingProof[] }>("/sales/proofs-waiting"),
  });
  const [state, setState] = useState<Record<string, string>>({});
  const remind = useMutation({
    mutationFn: (proofId: string) => api<{ sent: number; failed: string[] }>(`/proofs/${proofId}/remind`, { method: "POST" }),
    onMutate: (id) => setState((m) => ({ ...m, [id]: "sending" })),
    onSuccess: (out, id) => {
      setState((m) => ({ ...m, [id]: out.sent > 0 ? "sent" : "Couldn't send the reminder." }));
      void queryClient.invalidateQueries({ queryKey: ["sales", "proofs-waiting"] });
    },
    onError: (e, id) =>
      setState((m) => ({
        ...m,
        [id]: e instanceof ApiError ? ((e.body as { error?: string } | null)?.error ?? "Couldn't send the reminder.") : "Couldn't send the reminder.",
      })),
  });

  const proofs = q.data?.proofs ?? [];
  if (q.isLoading || q.isError || proofs.length === 0) return null;
  return (
    <section className="rounded-md border border-rule bg-white p-[22px]" aria-labelledby="proofs-waiting">
      <div className="mb-3.5 flex items-baseline justify-between gap-3">
        <h2 id="proofs-waiting" className="text-lg font-semibold tracking-[-0.4px] text-ink">Proofs waiting on the client.</h2>
        <span className="text-xs text-muted">
          {proofs.length} {proofs.length === 1 ? "proof" : "proofs"} · a day or more
        </span>
      </div>
      <ul>
        {proofs.map((p) => {
          const s = state[p.proofId];
          return (
            <li key={p.proofId} className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 border-b border-[#e8e8e5] py-3.5 first:pt-0 last:border-0 last:pb-0">
              <div className="min-w-0 flex-1">
                <p className="font-medium text-ink">
                  {p.clientName} · {p.item}
                  {p.version > 1 ? ` (v${p.version})` : ""}
                </p>
                <p className="mt-1 text-xs text-muted">
                  {p.projectName} · sent {timeAgo(p.sentAt)}
                  {p.viewedAt ? " · opened" : " · not opened yet"}
                  {p.lastReminderAt ? ` · reminded ${timeAgo(p.lastReminderAt)}` : ""}
                </p>
                {s && s !== "sending" && s !== "sent" && <p role="alert" className="mt-1 text-xs text-risk">{s}</p>}
              </div>
              <div className="flex items-center gap-2">
                {s === "sent" ? (
                  <span className="text-sm font-medium text-ok">Reminder sent</span>
                ) : (
                  <button type="button" className={btn} disabled={s === "sending"} onClick={() => remind.mutate(p.proofId)}>
                    {s === "sending" ? "Sending…" : "Send reminder"}
                  </button>
                )}
                <Link to={`/projects/${p.projectId}/pos`} className={btn}>
                  Open project
                </Link>
              </div>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
